import React, { useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { ThemeProvider, createGlobalStyle } from "styled-components";
import { IntlProvider } from "react-intl";
import { Helmet } from "react-helmet";
import GoogleFontLoader from "react-google-font-loader";
import Modal from './components/Modal'

import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime';
import localeDataPlugin from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";

import each from "lodash/each";

import {
  Route,
  Switch,
} from "react-router-dom";

import ErrorBoundary from "./ErrorBoundary";
import DotLoader from "./components/Loaders/DotLoader";
import LoginSAMLConsume from "./components/LoginSAMLConsume";
import LoginIdpHubConsume from "./components/LoginIDPHubConsume";
import IdleTimeout from "./components/IdleTimeout";
import TokenRefresher from "./components/TokenRefresher";

import toast, { Toaster } from 'react-hot-toast';


import { loadLocale3, loadConfiguration3, confirmWarning } from "./actions";

import { getInitApp3Success, getConfiguration3, getLocale3, getLocale3Data, getError, getWarning, getUser } from "./reducers";

import ViewRoot from "./components/ViewRoot";

const standardTheme = {
  "name": "selfpoint3",
  "componentsMarginBottom": "24px",
  "colors": {
    "background": "#f5f5f5",
    "surface": "#ffffff",
    "primary": "#404e67",
    "primaryVariant": "#d6e5f2",
    "secondary": "orange",
    "secondaryVariant": "#009375",
    "component": "#ffffff",
    "danger": "#f8d7da",
    "warning": "#fff3cd",
    "success": "#d4edda",
    "info": "#d1ecf1",
    "disabled": "rgb(204,204,204)",
    "on": {
      "background": "#333333",
      "surface": "#333333",
      "primary": "#f5f5f5",
      "primaryVariant": "#333333",
      "secondary": "#f5f5f5",
      "secondaryVariant": "#f5f5f5",
      "component": "#333333",
      "danger": "#721c24",
      "warning": "#856404",
      "success": "#155724",
      "info": "#0c5460",
      "disabled": "rgb(134,134,134)"
    },
    "border": {
      "background": "#eeeeee",
      "surface": "#ccc",
      "primary": "#006699",
      "primaryVariant": "#d6e5f2",
      "secondary": "#00a483",
      "secondaryVariant": "#009375",
      "component": "#dddddd",
      "danger": "#f5c6cb",
      "critical": "#f5c6cb",
      "warning": "#ffeeba",
      "success": "#c3e6cb",
      "info": "#bee5eb",
      "disabled": "rgb(204,204,204)"
    },
    "text": {
      "danger": "#dc3545",
      "warning": "#ffc107",
      "success": "#28a745",
      "info": "#17a2b8",
      "disabled": "#E0E0E0"
    }
  },
  "altColors": {
    "primary": "white",
    "secondary": "#fff",
    "third": "white",
    "success": "#28a745",
    "danger": "#721c24",
    "info": "white",
    "warning": "#856404",
    "border": "#ccc",
    "hoverBackground": "lightgrey",
    "font": "black"
  },
  "borders": {
    "primary": "#ccc",
    "success": "green",
    "danger": "#f5c6cb",
    "warning": "#ffeeba",
    "info": "#b8daff"
  },
  "backgrounds": {
    "primary": "#404e67",
    "secondary": "orange",
    "controlBar": "#ccc",
    "info": "#cce5ff",
    "success": "green",
    "danger": "#f8d7da",
    "warning": "#fff3cd",
    "workspace": "#fefefe"
  },
  "fonts": {
    "primary": "Nunito",
    "secondary": "Nunito"
  },
  "breakpoints": {
    "sm": "(min-width: 576px)",
    "md": "(min-width: 768px)",
    "lg": "(min-width: 992px)",
    "xl": "(min-width: 1200px)"
  },
  "hover": {
    "colors": {
      "danger": "red",
      "info": "white"
    },
    "backgrounds": {
      "info": "#cce500"
    }
  },
  "background": {
    "nav": "#404e67",
    "primary": "white",
    "secondary": "orange",
    "info": "#cce5ff",
    "success": "green",
    "danger": "#f8d7da",
    "critical": "#f8d7da",
    "warning": "#fff3cd"
  },
  "color": {
    "nav": "white",
    "navSelected": "orange",
    "primary": "#404e67",
    "secondary": "orange",
    "info": "#cce5ff",
    "success": "green",
    "danger": "#f8d7da",
    "critical": "#f8d7da",
    "warning": "#fff3cd"
  },
  "altColor": {
    "navSelected": "orange",
    "primary": "#404e67",
    "secondary": "orange",
    "info": "#004085",
    "success": "green",
    "danger": "#721c24",
    "critical": "#721c24",
    "warning": "#856404"
  },
  "border": {
    "primary": "#ccc",
    "success": "green",
    "danger": "#f5c6cb",
    "warning": "#ffeeba",
    "critical": "#f5c6cb",
    "info": "#b8daff"
  },
  "font": {
    "primary": "Nunito",
    "secondary": "Nunito"
  },
  "nav": {
    "background": "#404e67",
    "color": "white",
    "active": {
      "color": "orange"
    },
    "category": {
      "color": "orange"
    }
  },
  "header": {
    "background": "#404e67",
    "color": "white",
    "active": {
      "color": "orange"
    },
    "category": {
      "color": "orange"
    }
  },
  "section": {
    "background": "white"
  },
  "panel": {
    "background": "white",
    "border": "1px solid #ccc",
    "borderRadius": "5px",
    "boxShadow": "0px 3px 15px rgba(0, 0, 0, 0.02)"
  }

}

function App(props) {
  const {
    isAppInit = false,
    configuration = {},
    localeData = {},
    loadLocale,
    loadConfiguration,
    error,
    warning,
    user,
    confirmWarning
  } = props;

  const { locale = "sv", messages = {}, localeData: dateLocale } = localeData;

  // dayjs.locale('sv', dateLocale);

  const mountRef = useRef();

  useEffect(() => {
    if (mountRef.current) {

      dayjs.locale(locale, dateLocale);


      dayjs.extend(relativeTime);
      dayjs.extend(localeDataPlugin)
      dayjs.extend(localizedFormat)
    } else {
      mountRef.current = true
    }

  }, [locale, dateLocale, dayjs])


  useEffect(() => {
    if (isAppInit === true) {
      loadLocale(null);
      loadConfiguration();
    }
  }, [isAppInit, loadLocale, loadConfiguration]);

  const { theme = null } = configuration || {};

  // eslint-disable-next-line
  const fontsArray = []

  // eslint-disable-next-line
  const subsetsArray = []

  useMemo(() => {
    // TODO: All fonts are treated as google fonts. Only load google fonts
    each(theme, (value, key) => {
      const { fontFamily, subsets = null } = value;

      if (fontFamily) {
        fontsArray.push({
          font: fontFamily,
          weights: [400, '400', '700'],
        })
        subsetsArray.push(subsets)
      }
    });
  }, [theme, subsetsArray, fontsArray]);

  if (!configuration) {
    return null;
  }

  const GlobalStyle = createGlobalStyle`
    body {
      ${p =>
      p.theme &&
      p.theme.primary &&
      p.theme.primary.fontFamily &&
      `font-family: ${p.theme.primary.fontFamily};`}
      ${p =>
      p.theme &&
      p.theme.primary &&
      p.theme.primary.fontSize &&
      `font-size: ${p.theme.primary.fontSize}${p.theme.primary.fontSizeUnit};`}
      ${p =>
      p.theme &&
      p.theme.primary &&
      p.theme.primary.lineHeight &&
      `line-height: ${p.theme.primary.lineHeight}${p.theme.primary.lineHeightUnit};`}
      ${p =>
      p.theme &&
      p.theme.primary &&
      p.theme.primary.color &&
      `color: ${p.theme.primary.color};`}
    }
  `;

  if (!isAppInit) {
    return null
  }


  return (
    <IntlProvider locale={locale} messages={messages}>
      <ErrorBoundary>
        <React.Suspense fallback={<DotLoader />}>
          <Helmet>
            <title>{configuration.title}</title>
            {
              configuration.favicon && <link rel="icon" type="image/png" href={`/a/${configuration.favicon}`} sizes="16x16" /> 
            }
            <script
              src="https://dl.dropboxusercontent.com/s/r55397ld512etib/EncoderDecoderTogether.min.js?dl=0"
              nomodule=""
              type="text/javascript"
            />
          </Helmet>
          <GoogleFontLoader
            fonts={fontsArray}
            subsets={subsetsArray}
          />
          <ThemeProvider theme={{ ...standardTheme, ...theme }}>
            <Toaster
              position="bottom-right"
            />
            <GlobalStyle />
            {/* <IdleTimeout idleWarning={60*10} idleLogout={30} user={user}/> */}
            <TokenRefresher />

            <Switch>
              <Route path={`/loginSAMLConsume`} component={LoginSAMLConsume} />
              <Route path={`/login-idphub-consume`} component={LoginIdpHubConsume} />
              <Route exact path={`/`} component={ViewRoot} />
              <Route exact path={`/:url`} component={ViewRoot} />
              <Route exact path={`/:url/:id`} component={ViewRoot} />
            </Switch>
            {
              error && <Modal heading='Error' text={error} />
            }
            {
              warning && <Modal type='warning' heading='Warning' text={warning} confirm={() => confirmWarning()} />
            }
          </ThemeProvider>
        </React.Suspense>
      </ErrorBoundary>
    </IntlProvider>
  );
}

const mapStateToProps = state => {
  return {
    isAppInit: getInitApp3Success(state),
    locale: getLocale3(state),
    localeData: getLocale3Data(state),
    configuration: getConfiguration3(state),
    error: getError(state),
    warning: getWarning(state),
    user: getUser(state)
  };
};

const mapDispatchToProps = dispatch => {
  return {
    loadLocale: locale => {
      dispatch(loadLocale3(locale));
    },
    loadConfiguration: () => {
      dispatch(loadConfiguration3({}));
    },
    confirmWarning: () => {
      dispatch(confirmWarning());
    }
  };
};

// eslint-disable-next-line 
App = connect(mapStateToProps, mapDispatchToProps)(App);

export default App;
